import '../CreateEditDialog.scss';

import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { usePhoneOptionsContext } from '../../../../../../context/PhoneOptionsContext';
import useAxiosHook from '../../../../../../hooks/useAxiosHook';
import { WithData } from '../../../../../../types/api';
import {
  MunicipalityCollection,
  MunicipalityPlaceCollection,
} from '../../../../../../types/api/municipalities';
import { PlacesStreetCollection } from '../../../../../../types/api/places';
import { isStreetFreeInputAllowed } from '../../../../../../utils/constants/misc';
import { invalidPhoneNumberCharactersRegex } from '../../../../../../utils/constants/phoneNumbers';
import {
  formatMobileNumber,
  formatPhoneNumber,
  getPhoneorMobileNumberRegion,
} from '../../../../../../utils/helpers/phoneNumbers';
import FieldWithErrors from '../../../../../Forms/ReactHookForm/FieldWithErrors';

function GeneralInfo() {
  const { t } = useTranslation();

  const { getValues, setValue, watch } = useFormContext();
  const values = watch();

  const { countryOptionTemplate, languages, selectedCountryTemplate } =
    usePhoneOptionsContext();

  const { data: municipalities, isLoading: isMunicipalitiesLoading } =
    useAxiosHook<WithData<MunicipalityCollection>>('/municipalities?limit=0');

  const {
    data: places,
    reload: reloadPlaces,
    isLoading: isPlacesLoading,
  } = useAxiosHook<MunicipalityPlaceCollection>(
    `/municipalities/${values.address.municipalityId}/places?limit=0`,
    {
      skipWhen: !values.address.municipalityId,
      skipCache: true,
    }
  );

  const {
    data: streets,
    reload: reloadStreets,
    isLoading: isStreetsLoading,
  } = useAxiosHook<WithData<PlacesStreetCollection>>(
    `/places/${values.address.placeId}/streets?limit=0`,
    {
      skipWhen: !values.address.placeId,
      skipCache: true,
    }
  );

  const municipalitiesOptions = useMemo(
    () =>
      municipalities && municipalities.data.length > 0
        ? municipalities?.data?.map((m) => ({
            label: m.name,
            value: m.id,
          }))
        : [],
    [municipalities, values.address.municipalityId]
  );

  const placesOptions = useMemo(
    () =>
      places && places.length > 0
        ? places?.map((m) => ({
            label: m.place_name,
            value: m.place_id,
            postal_code: m.zip,
          }))
        : [],
    [places, values.address.municipalityId]
  );

  const streetsOptions = useMemo(
    () =>
      streets && streets?.data?.length > 0
        ? streets?.data?.map((m) => ({ label: m.name, value: m.id }))
        : [],
    [streets, values.address.placeId]
  );

  return (
    <div className="general-info-tab p-fluid">
      <div className="p-grid">
        <div className="p-col-12 p-md-6">
          <FieldWithErrors name="name" label={t('Name')}>
            <Controller
              name="name"
              render={({ field }) => (
                <InputText
                  id="name"
                  name="name"
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </FieldWithErrors>

          <FieldWithErrors name="email" label={t('Email')}>
            <Controller
              name="email"
              render={({ field }) => (
                <InputText
                  id="email"
                  name="email"
                  value={field.value}
                  onChange={field.onChange}
                />
              )}
            />
          </FieldWithErrors>

          <FieldWithErrors name="taxNumber" label={t('Tax number')}>
            <Controller
              name="taxNumber"
              render={({ field }) => (
                <InputText
                  id="taxNumber"
                  name="taxNumber"
                  value={field.value}
                  onChange={field.onChange}
                  data-cy="taxNumber"
                />
              )}
            />
          </FieldWithErrors>
          <FieldWithErrors name="ssNumber" label={t('SS number')}>
            <Controller
              name="ssNumber"
              render={({ field }) => (
                <InputText
                  id="ssNumber"
                  name="ssNumber"
                  value={field.value}
                  onChange={field.onChange}
                  data-cy="ssNumber"
                />
              )}
            />
          </FieldWithErrors>

          <span className="dialog-section-divider">{t('Contact Info')}</span>
          <FieldWithErrors name="mobile" label={t('Mobile')}>
            <div className="p-inputgroup number-group">
              <Controller
                name="mobile_region"
                render={({ field }) => (
                  <Dropdown
                    style={{ padding: 0, width: '5rem' }}
                    id="mobile_region"
                    options={languages}
                    itemTemplate={countryOptionTemplate}
                    valueTemplate={selectedCountryTemplate}
                    data-cy="mobile_region"
                    {...field}
                  />
                )}
              />
              <Controller
                name="mobile"
                render={({ field }) => (
                  <InputText
                    name="mobile"
                    style={{ width: '100%' }}
                    value={formatMobileNumber(
                      getValues()?.mobile,
                      getValues()?.mobile_region ||
                        getPhoneorMobileNumberRegion(getValues()?.mobile)
                    )}
                    onChange={(e) => {
                      field.onChange(
                        e.target.value?.replace(
                          invalidPhoneNumberCharactersRegex,
                          ''
                        )
                      );
                    }}
                    className="data-cy-mobile"
                  />
                )}
              />
            </div>
          </FieldWithErrors>

          <FieldWithErrors name="phone" label={t('Phone')}>
            <div className="p-inputgroup">
              <Controller
                name="phone_region"
                render={({ field }) => (
                  <Dropdown
                    style={{ padding: 0, width: '5rem' }}
                    id="phone_region"
                    options={languages}
                    itemTemplate={countryOptionTemplate}
                    valueTemplate={selectedCountryTemplate}
                    data-cy="phone_region"
                    {...field}
                  />
                )}
              />
              <Controller
                name="phone"
                render={({ field }) => (
                  <InputText
                    name="phone"
                    style={{ width: '100%' }}
                    value={formatPhoneNumber(
                      getValues()?.phone,
                      getValues()?.phone_region ||
                        getPhoneorMobileNumberRegion(getValues()?.phone)
                    )}
                    onChange={(e) => {
                      field.onChange(
                        e.target.value?.replace(
                          invalidPhoneNumberCharactersRegex,
                          ''
                        )
                      );
                    }}
                    className="data-cy-telefon"
                  />
                )}
              />
            </div>
          </FieldWithErrors>
        </div>

        <div className="p-col-12 p-md-6">
          <FieldWithErrors
            name="address.municipalityId"
            label={t('Municipality')}
          >
            <Controller
              name="address.municipalityId"
              render={({ field }) => (
                <Dropdown
                  id="address.municipalityId"
                  name="address.municipalityId"
                  options={municipalitiesOptions}
                  value={field.value}
                  onChange={(e) => {
                    if (e.value) {
                      field.onChange(e.value);
                      const items = municipalitiesOptions.find(
                        (s) => s.value === e.value
                      );
                      setValue('address.municipalityName', items?.label || '');

                      reloadPlaces({
                        url: `municipalities/${e.value}/places?limit=0`,
                      });

                      setValue('address.placeId', null);
                      setValue('address.placeName', '');
                      setValue('address.streetId', null);
                      setValue('address.streetName', '');
                      setValue('address.housenumber', '');
                      setValue('address.entrance', '');
                      setValue('address.apartment', '');
                    }
                  }}
                  filter
                  filterPlaceholder={t('Search')}
                  placeholder={isMunicipalitiesLoading ? t('Loading...') : ''}
                  className="data-cy-municipalityId"
                />
              )}
            />
          </FieldWithErrors>

          <FieldWithErrors name="address.placeId" label={t('Place')}>
            <Controller
              name="address.placeId"
              render={({ field }) => (
                <Dropdown
                  id="address.placeId"
                  name="address.placeId"
                  options={placesOptions}
                  value={field.value}
                  onChange={(e) => {
                    if (e.value) {
                      field.onChange(e.value);
                      const items = placesOptions.find(
                        (s) => s.value === e.value
                      );
                      setValue('address.streetName', items?.label || '');

                      reloadStreets({
                        url: `places/${e.value}/streets?limit=0`,
                      });
                    } else {
                      setValue('address.placeId', null);
                      setValue('address.placeName', '');
                      setValue('address.streetId', null);
                      setValue('address.streetName', '');
                      setValue('address.housenumber', '');
                      setValue('address.entrance', '');
                      setValue('address.apartment', '');
                    }
                  }}
                  filter
                  filterPlaceholder={t('Search')}
                  placeholder={isPlacesLoading ? t('Loading...') : ''}
                  disabled={!values.address.municipalityId || isPlacesLoading}
                  className="data-cy-placeId"
                />
              )}
            />
          </FieldWithErrors>

          {isStreetFreeInputAllowed ? (
            <FieldWithErrors name="address.rawName" label={t('Address')}>
              <Controller
                name="address.rawName"
                render={({ field }) => (
                  <InputText
                    id="address.rawName"
                    name="address.rawName"
                    value={field.value}
                    onChange={field.onChange}
                    disabled={
                      !values.address.municipalityId || !values.address.placeId
                    }
                    max={256}
                  />
                )}
              />
            </FieldWithErrors>
          ) : (
            <>
              <FieldWithErrors name="address.streetId" label={t('Street')}>
                <Controller
                  name="address.streetId"
                  render={({ field }) => (
                    <Dropdown
                      id="address.streetId"
                      name="address.streetId"
                      options={streetsOptions}
                      value={field.value}
                      onChange={(e) => {
                        if (e.value) {
                          field.onChange(e.value);
                          const items = streetsOptions.find(
                            (s) => s.value === e.value
                          );
                          setValue('address.streetName', items?.label || '');
                        } else {
                          setValue('address.streetId', null);
                          setValue('address.streetName', '');
                          setValue('address.housenumber', '');
                          setValue('address.entrance', '');
                          setValue('address.apartment', '');
                        }
                      }}
                      filter
                      filterPlaceholder={t('Search')}
                      placeholder={isStreetsLoading ? t('Loading...') : ''}
                      disabled={!values.address.placeId || isStreetsLoading}
                      className="data-cy-streetId"
                    />
                  )}
                />
              </FieldWithErrors>

              <div className="street-numbers">
                <FieldWithErrors
                  name="address.housenumber"
                  label={t('Street No.')}
                >
                  <Controller
                    name="address.housenumber"
                    render={({ field }) => (
                      <InputText
                        id="address.housenumber"
                        name="address.housenumber"
                        value={field.value}
                        onChange={field.onChange}
                        disabled={!values.address.streetId}
                      />
                    )}
                  />
                </FieldWithErrors>

                <span className="divider">/</span>

                <FieldWithErrors
                  name="address.entrance"
                  label={t('Entrance No.')}
                >
                  <Controller
                    name="address.entrance"
                    render={({ field }) => (
                      <InputText
                        id="address.entrance"
                        name="address.entrance"
                        value={field.value}
                        onChange={field.onChange}
                        disabled={!values.address.streetId}
                      />
                    )}
                  />
                </FieldWithErrors>

                <span className="divider">/</span>

                <FieldWithErrors name="address.apartment" label={t('Flat No.')}>
                  <Controller
                    name="address.apartment"
                    render={({ field }) => (
                      <InputText
                        id="address.apartment"
                        name="address.apartment"
                        value={field.value}
                        onChange={field.onChange}
                        disabled={!values.address.streetId}
                      />
                    )}
                  />
                </FieldWithErrors>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default GeneralInfo;
